import {
  computed, ref, watch,
} from 'vue';

import { UpcParser as upcParser } from '@common-vue/media-card-large';
import { useBrand } from '@composables/useBrand';
import { useCommonProductGetters } from '@composables/useCommonProductGetters';
import { useEnvProps } from '@composables/useEnvProps';
import { useMutation } from '@composables/utils/useMutation';
import { useProduct } from '@modules/product';
import { getUserIds } from '@utils/cookiesUtils';
import { OrderSwatchesService } from '../services/OrderSwatches.service';
import { getItemsArray } from '../utils/getItemsArray';
import { tagOrderSwatchesComplete } from '../../../features/analytics/orderSwatches';

/**
 * @param {Color[]} colors
 * @param {import('vue').Ref<number | null>} selectedPrice
 * @param {import('vue').Ref<number | null>} selectedOrientationId
 */
export function useOrderSwatches(colors = [], selectedPrice, selectedOrientationId) {
  const { isMcom } = useBrand();
  const { isSignedIn, userId, userGuid } = getUserIds();
  const { productData: product, productId } = useProduct();
  const { productName } = useCommonProductGetters();
  const { upcMap } = upcParser(product);
  const { hostlink } = useEnvProps();

  const firstSectionTitle = isMcom
    ? '1. Select Your Swatches'
    : 'SELECT YOUR SWATCHES';

  const showSuccessfulTemplate = ref(false);
  const isSwatchesNotificationVisible = ref(false);
  const emailAddress = ref('');

  // Show first checkbox as selected
  const checkboxValue = ref(colors[0]?.id ? [colors[0]?.id] : []);
  const checkboxOptions = computed(() => colors.map((color) => ({
    id: color.id,
    label: color.name,
    ariaLabel: color.name,
    value: color.id,
  })));

  /**
   *
   * @param {Object} values - An object containing user information.
   * @param {string} values.fullName
   * @param {string} values.email
   * @param {string} values.address
   * @param {string} values.city
   * @param {string} values.zipCode
   * @param {string} values.state
   */

  const getPayloadObject = (values = {}) => {
    const {
      fullName, email, address, city, zipCode, state,
    } = values;

    const shippingData = {
      name: fullName,
      emailAddress: email,
      address: {
        addressLine1: address,
        city,
        state,
        zipCode,
      },
    };

    return {
      swatchesOrderCapture: {
        items: getItemsArray(product, checkboxValue.value, productId, upcMap, selectedOrientationId),
        vendorEmail: product?.detail?.swatchEmail,
        siteId: isMcom ? 'MCOM' : 'BCOM',
        user: isSignedIn ? { userID: userId, userGUID: userGuid } : {},
        contact: shippingData,
      },
    };
  };

  const triggerSwatchesNotification = () => {
    if (checkboxValue.value?.length === 0) {
      isSwatchesNotificationVisible.value = true;
    }
  };

  const orderSwatchesService = new OrderSwatchesService();

  const orderSwatchesMutation = useMutation(
    orderSwatchesService.orderSwatches.bind(orderSwatchesService),
    {
      onBefore() {
        showSuccessfulTemplate.value = false;
      },
      onSuccess() {
        showSuccessfulTemplate.value = true;
        tagOrderSwatchesComplete(emailAddress.value, productId, selectedPrice?.value);
        emailAddress.value = '';
      },
    },
  );

  const submitOrderSwatchesForm = async (values = {}) => {
    if (checkboxValue.value?.length === 0) {
      isSwatchesNotificationVisible.value = true;
      return;
    }

    const payloadObj = getPayloadObject(values);

    emailAddress.value = payloadObj.swatchesOrderCapture.contact.emailAddress;

    await orderSwatchesMutation.mutate({
      hostlink,
      productId,
      payloadObj,
    });
  };

  watch(checkboxValue.value, () => {
    isSwatchesNotificationVisible.value = false;
  });

  watch(orderSwatchesMutation.isLoading, (newValue) => {
    if (!newValue) return;
    orderSwatchesMutation.data.value = null;
    showSuccessfulTemplate.value = false;
    orderSwatchesMutation.resetError();
  });

  return {
    checkboxValue,
    checkboxOptions,
    firstSectionTitle,
    getPayloadObject,
    error: orderSwatchesMutation.error,
    isLoading: orderSwatchesMutation.isLoading,
    isSwatchesNotificationVisible,
    productId,
    productName,
    showSuccessfulTemplate,
    submitOrderSwatchesForm,
    triggerSwatchesNotification,
  };
}

export default useOrderSwatches;
